<template>
  <dropdown
    ref="dropdownRef"
    class="ifp-filter-customers"
    title="Customers"
    @open="handleDropdownOpen();getCustomers()"
  >
    <base-ifp-filter
      label="Customer"
      clearable
      @clear-clicked="clearSelectedIDs()"
    >
      <template #value-display>
        <!-- Customers loaded (e.g. dropdown clicked) -->
        <template v-if="customersLoaded">
          <!-- One customer selected -->
          <div v-if="hasLoneCustomerSelection" class="lone-customer-value-display">
            <avatar :person="selectedCustomers[0]" :size="18" />
            <span>{{ selectedCustomers[0].name}}</span>
          </div>
          <!-- N customers selected -->
          <avatar-gallery 
            v-else-if="hasSelectedCustomers"
            :people="selectedCustomers" 
            :max-num-avatars="3" 
            :size="18" 
            stack-tightly
            suppress-avatar-tooltip
            suppress-remaining-people-tooltip
          />
        </template>
        <!-- Customers not yet loaded -->
        <template v-else>
          <!-- One customer selected -->
          <template v-if="hasOneSelectedID">
            <!-- Pull from lone get call -->
            <span v-if="isPrefetchingLoneCustomer" class="lone-customer-value-display__pseudo-loader">…</span>
            <div v-else class="lone-customer-value-display">
              <avatar :person="prefetchedLoneCustomer" :size="18" />
              <span>{{ prefetchedLoneCustomer.name}}</span>
            </div>
          </template>
          <!-- N customers selected (just show "N customers" to avoid hydrating by eager load of customers) -->
          <template v-else-if="hasSelectedIDs">
            <span>{{ selectedIds.length }} customers</span>
          </template>
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <div>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Search by name or email…"
          show-small-text
        />
        <loading-placeholder v-if="exposeResults && !customersLoaded" :lines="10" />
        <ul v-else-if="exposeResults && hasFilteredCustomers">
          <li
            v-for="customer in filteredOptions"
            :key="customer.id"
            @click="handleSelection(customer.id)"
          >
            <avatar :size="20" :person="customer" />
            <span :class="{'selected': isSelected(customer.id)}">
              <template v-if="customer.name===customer.email">{{ customer.email }}</template>
              <template v-else><strong>{{ customer.name }}</strong> &lt;{{ customer.email }}&gt;</template>
            </span>
          </li>
        </ul>
        <span v-else-if="exposeResults">
          No customers found
        </span>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */
import CustomersAPI from '@/api/modules/customers.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'IfpFilterCustomers',

  extends: SearchableDropdownMixin,

  props: {
    mailboxId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isPrefetchingLoneCustomer: false,
      customersLoaded: false,
      customers: [] as IPersonWithEmail[],
      prefetchedLoneCustomer: null as IPersonWithEmail,

      /* SearchableDropdownMixin configuration */
      allowMultipleSelections: true,
      minItemsForFilterInput: 0,
    }
  },
  computed: {
    totalOptionsCount(): number {
      return this.customers.length
    },
    filteredOptions(): IPersonWithEmail[] {
      if (this.filterQuery.length < 2) {
        return []
      }

      return this.customers
        .filter(c => {
          return (
            c.name.toLowerCase().includes(this.filterQuery.toLowerCase()) ||
            c.email.toLowerCase().includes(this.filterQuery.toLowerCase())
          )
        })
        .slice(0, 50)
    },
    exposeResults(): boolean {
      return this.filterQuery.length > 1
    },
    hasLoneCustomerSelection(): boolean {
      return this.selectedCustomers.length === 1
    },
    hasSelectedCustomers(): boolean {
      // Unvetted from backend, for eager label display...
      return this.selectedCustomers.length > 0
    },
    hasSelectedIDs(): boolean {
      // Unvetted from backend, for eager label display...
      return this.selectedIds.length > 0
    },
    hasOneSelectedID(): boolean {
      // Unvetted from backend, for eager label display...
      return this.selectedIds.length === 1
    },
    selectedCustomers(): IPersonWithEmail[] {
      if (!this.customersLoaded) {
        return []
      }
      return this.customers.filter(c => this.selectedIds.includes(c.id))
    },
    hasFilteredCustomers(): boolean {
      return this.filteredOptions.length > 0
    },
  },

  watch: {
    selectedIds: {
      immediate: true,
      handler(val) {
        // Prefetch the one customer for the value display
        if (!this.customersLoaded) {
          if (this.hasOneSelectedID) {
            this.prefetchLoneCustomer(this.selectedIds[0])
          }
        }
      },
    },
  },
  methods: {
    async getCustomers() {
      if (this.customersLoaded) {
        return
      }
      try {
        if (this.mailboxId) {
          this.customers = await MailboxesAPI.get_Customers(this.mailboxId)
        } else {
          this.customers = await CustomersAPI.get_All()
        }
        this.customersLoaded = true
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async prefetchLoneCustomer(id: number) {
      try {
        this.isPrefetchingLoneCustomer = true
        this.prefetchedLoneCustomer = await CustomersAPI.get_ContactInfo(id)
        return
      } catch (error) {
        // If we're here, the customer ID doesn't exist. Clear it...
        this.clearSelectedIDs()
      } finally {
        this.isPrefetchingLoneCustomer = false
      }
    },
  },
})
</script>

<style lang="scss">
.ifp-filter-customers {
  .lone-customer-value-display {
    display: flex;
    align-items: center;
    width: 100%;

    > span {
      @include single-line-ellipsis;
    }

    &__pseudo-loader {
      color: $color--gray-30;
    }

    @include flex-row-horizontal-spacing(4px);
  }
}
</style>
